import { IconArrowUpBold, Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Typography } from '@common/styles';
import { Router } from '@domains/pdp-common';

import { FooterButtonGroup } from './FooterButtonGroup';

type SellerInfo = {
  company_name: string | null | undefined;
  representative_name: string | null | undefined;
  company_address: string | null | undefined;
  registration_number: string | null | undefined;
  customer_center_tel: string | null;
  company_email: string | null | undefined;
  online_sales_license_number: string | null;
  terms_of_use_policy_url: string;
  privacy_policy_url: string;
  kakao_cs_url?: string | null;
  naver_cs_url?: string | null;
};

interface Props {
  country: string;
  sellerInfo: SellerInfo;
  isOpened: boolean;
  toggle: () => void;
}
export const SellerInfo: React.FC<React.PropsWithChildren<Props>> = ({ country, sellerInfo, isOpened, toggle }) => {
  const renderRow = (label: string, value?: string | null) => {
    if (!value) {
      return null;
    }

    return (
      <ContactRow className={Typography.CAPTION_12_MEDIUM}>
        <ContactLabel>{label}</ContactLabel>
        <ContactValues>{value}</ContactValues>
      </ContactRow>
    );
  };

  return (
    <Container>
      <Title onClick={toggle} isOpened={isOpened}>
        판매자 정보
        <IconArrowUpBold v3Color='gray950' width={24} height={24} className='icon' />
      </Title>
      <Contents isOpened={isOpened}>
        {country !== 'KR' && (
          <GuideBox>
            <ZigzagContact className={Typography.CAPTION_12_REGULAR}>
              문의 사항은 지그재그 고객센터 <span className={Typography.CAPTION_12_SEMIBOLD}>help@kakaostyle.com</span>{' '}
              로 문의해 주세요.
            </ZigzagContact>
          </GuideBox>
        )}
        <RowContainer>
          {renderRow('업체명', sellerInfo.company_name)}
          {renderRow('대표자명', sellerInfo.representative_name)}
          {renderRow('사업장 소재지', sellerInfo.company_address)}
          {renderRow('사업자등록번호', sellerInfo.registration_number)}
          <ContactRow className={Typography.CAPTION_12_MEDIUM}>
            {/* MEMO: 통신판매업신고번호 (신고번호 데이터를 따로 저장하지 않음)
            사업자번호를 활용하여 새 페이지(웹뷰)로 통신판매업신고번호 및 관련 정보를 유저가 확인하도록 제공
             https://croquis.atlassian.net/browse/COMSYSREQ-41 */}
            <ContactLabel>통신판매업 신고번호</ContactLabel>
            <UnderlineValue
              onClick={() => {
                if (sellerInfo.registration_number) {
                  Router.open(`http://www.ftc.go.kr/bizCommPop.do?wrkr_no=${sellerInfo.registration_number}`, {
                    title: '사업자 정보 확인',
                  });
                }
              }}
            >
              {sellerInfo.online_sales_license_number || sellerInfo.registration_number}
            </UnderlineValue>
          </ContactRow>
          {renderRow('전화번호', sellerInfo.customer_center_tel)}
          {renderRow('이메일', sellerInfo.company_email)}
        </RowContainer>
        <FooterButtonGroup>
          <Text
            as='span'
            variant='Body_13_Medium'
            color='textPrimary'
            onClick={() => Router.open(sellerInfo.terms_of_use_policy_url, { title: '이용약관' })}
          >
            이용약관
          </Text>
          <Text
            as='span'
            variant='Body_13_Bold'
            color='textPrimary'
            onClick={() => Router.open(sellerInfo.privacy_policy_url, { title: '개인정보처리방침' })}
          >
            개인정보처리방침
          </Text>
        </FooterButtonGroup>
      </Contents>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  padding: 0 16px;
  -webkit-tap-highlight-color: transparent;
  overflow: hidden;
`;

const Contents = styled.div<{ isOpened?: boolean }>`
  height: 0;
  box-sizing: border-box;
  overflow: hidden;

  ${({ isOpened }) =>
    isOpened &&
    css`
      padding: 0 0 16px 0;
      height: auto;
    `}
`;

const GuideBox = styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  margin: 6px 0 16px;
  border-radius: 4px;
  background-color: ${vars.v3Color.gray100};
  color: ${vars.v3Color.gray400};
`;

const ZigzagContact = styled.div`
  width: 100%;
  text-align: center;
  line-height: 1.4;

  span {
    color: ${vars.v3Color.gray700};
  }
`;

const RowContainer = styled.div`
  padding-bottom: 12px;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: flex-start;
  min-height: 30px;
  padding: 8px 0;
`;

const ContactLabel = styled.span`
  max-width: 120px;
  width: 100%;
  padding-right: 5px;
  color: ${vars.v3Color.gray500};
`;

const ContactValues = styled.span`
  margin-right: 12px;
  word-break: keep-all;
  color: ${vars.v3Color.gray700};

  a {
    text-decoration: underline;
  }
`;

const UnderlineValue = styled(ContactValues)`
  text-decoration: underline;
  font-weight: 700;
`;

const Title = styled.p<{ isOpened?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  font-weight: 600;

  .icon {
    position: absolute;
    right: 12px;
    transform: rotate(180deg);
  }

  ${({ isOpened }) =>
    isOpened &&
    css`
      .icon {
        transform: rotate(0deg);
      }
    `}
`;
